import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import { ThemeProvider } from '@mui/material/styles';
import { Box, CircularProgress, CssBaseline } from '@mui/material';
import theme from './utils/theme';
import CreateWallet from './components/CreateWallet';
import Dashboard from './components/Dashboard';
import Governance from './components/Governance';
import Validators from './components/Validators';
import BootstrapNodes from './components/BootstrapNodes';
import Delegators from './components/Delegators';
import GovernanceAvo from './components/GovernanceAvo';
import StakingAvo from './components/StakingAvo';
import PerformanceAvo from './components/PerformanceAvo';
import Send from './components/Send';
import TransactionHistory from './components/TransactionHistory';
import NetworkSelector from './components/NetworkSelector';
import AvoNetworkSelector from './components/AvoNetworkSelector';
import ExportBackup from './components/ExportBackup';
import Setup2FA from './components/Setup2FA';
import Login from './components/Login';
import { getFromWalletDat } from './utils/walletDat';
import './App.css';


function App() {
  const [wallet, setWallet] = useState(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [hasWallet, setHasWallet] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const walletData = await getFromWalletDat();
        const encryptedWallet = walletData.cryptoWallet;
        const sessionActive = walletData.sessionActive;

        setHasWallet(!!encryptedWallet);
        
        // Verificar si hay datos de sesión en localStorage (prioridad sobre walletDat)
        const privateKey = localStorage.getItem('privateKey');
        const mnemonic = localStorage.getItem('mnemonic');
        const address = localStorage.getItem('address');
        
        // Si hay privateKey y address en localStorage, la sesión está activa
        if (privateKey && address) {
          setWallet({
            privateKey,
            mnemonic: mnemonic || null,
            address
          });
          setIsSessionActive(true);
        } else {
          // Si no hay datos en localStorage, verificar sessionActive de walletDat
          const isActive = sessionActive === 'true';
          setIsSessionActive(isActive);
        }
      } catch (err) {
        console.error('Error en initializeApp:', err);
        setHasWallet(!!localStorage.getItem('wallet.dat'));
        
        // Intentar restaurar sesión desde localStorage incluso si walletDat falla
        const privateKey = localStorage.getItem('privateKey');
        const address = localStorage.getItem('address');
        const mnemonic = localStorage.getItem('mnemonic');
        
        if (privateKey && address) {
          setWallet({
            privateKey,
            mnemonic: mnemonic || null,
            address
          });
          setIsSessionActive(true);
        } else {
          setIsSessionActive(false);
        }
      } finally {
        setIsLoading(false);
      }
    };
    initializeApp();
  }, []);

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
          <Routes>
            <Route
              path="/"
              element={
                hasWallet ? (
                  isSessionActive ? <Navigate to="/avo-dashboard" /> : <Navigate to="/login" />
                ) : (
                  <Navigate to="/create-wallet" />
                )
              }
            />
            <Route path="/login" element={<Login setWallet={setWallet} setIsSessionActive={setIsSessionActive} />} />
            <Route
              path="/create-wallet"
              element={
                hasWallet && !isSessionActive ? (
                  <Navigate to="/login" />
                ) : (
                  <CreateWallet setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                )
              }
            />
            <Route
              path="/dashboard"
              element={
                isSessionActive ? (
                  <Dashboard wallet={wallet} setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/avo-dashboard"
              element={
                isSessionActive ? (
                  <Dashboard wallet={wallet} setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/send"
              element={
                isSessionActive ? (
                  <Send wallet={wallet} setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/history"
              element={isSessionActive ? <TransactionHistory wallet={wallet} /> : <Navigate to="/login" />}
            />
            <Route
              path="/network"
              element={isSessionActive ? <NetworkSelector wallet={wallet} /> : <Navigate to="/login" />}
            />
            <Route
              path="/avo-network"
              element={isSessionActive ? <AvoNetworkSelector wallet={wallet} /> : <Navigate to="/login" />}
            />
            <Route
              path="/governance"
              element={
                isSessionActive ? (
                  <Governance wallet={wallet} setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/validators"
              element={
                isSessionActive ? (
                  <Validators wallet={wallet} setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/bootstrap-node"
              element={
                isSessionActive ? (
                  <BootstrapNodes wallet={wallet} setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/delegators"
              element={
                isSessionActive ? (
                  <Delegators wallet={wallet} setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/staking"
              element={
                isSessionActive ? (
                  <StakingAvo wallet={wallet} setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/performance"
              element={
                isSessionActive ? (
                  <PerformanceAvo wallet={wallet} setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/backup"
              element={isSessionActive ? <ExportBackup wallet={wallet} /> : <Navigate to="/login" />}
            />
            <Route
              path="/setup2fa"
              element={
                isSessionActive ? (
                  <Setup2FA wallet={wallet} setWallet={setWallet} setIsSessionActive={setIsSessionActive} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route path="*" element={<Navigate to={isSessionActive ? "/avo-dashboard" : "/login"} />} />
          </Routes>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
